import type {
	Armor,
	ArmorPassive,
	Booster,
	ClassDefinition,
	ClassLoadout,
	LoadoutItem,
	Stratagem,
	Weapon,
} from "@hd2/data";
import type { LoadoutSlotKey } from "../../../hooks/use-current-loadout.ts";
import type { UseItemsResult } from "../../../hooks/use-items.ts";

export const SLOT_TO_LOADOUT_KEY: Record<LoadoutSlotKey, keyof ClassLoadout> = {
	primary: "primaries",
	secondary: "secondaries",
	grenade: "grenades",
	armor: "armorPassives",
	stratagems: "stratagems",
	booster: "boosters",
};

export function isSlugValidForClass(
	slotKey: LoadoutSlotKey,
	slug: string,
	cls: ClassDefinition,
	items?: UseItemsResult,
): boolean {
	if (cls.role === "Wildcard") {
		return true;
	}
	if (slotKey === "armor") {
		const passiveSlugs = new Set(Object.keys(cls.loadout.armorPassives ?? {}));
		if (!items) {
			return false;
		}
		const forbiddenWeights = new Set(cls.forbiddenArmorWeightClasses ?? []);
		for (const passive of items.passives) {
			if (!passiveSlugs.has(slugify(passive.name))) {
				continue;
			}
			for (const armor of passive.armors) {
				if (forbiddenWeights.has(armor.type)) {
					continue;
				}
				if (slugify(armor.name) === slug) {
					return true;
				}
			}
		}
		return false;
	}
	const loadoutKey = SLOT_TO_LOADOUT_KEY[slotKey];
	return slug in (cls.loadout[loadoutKey] ?? {});
}

function slugify(name: string): string {
	return name
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, "-")
		.replace(/(^-|-$)/g, "");
}

const MODEL_PREFIX_RE = /^[A-Za-z/]+[-]?[A-Za-z0-9/]*\d[A-Za-z0-9]*\s+(.+)$/;

export function shortName(name: string): string {
	const m = name.match(MODEL_PREFIX_RE);
	if (m) {
		return m[1];
	}
	if (name.startsWith("Orbital ")) {
		return name.slice(8);
	}
	if (name.startsWith("Eagle ")) {
		return name.slice(6);
	}
	return name;
}

export interface ResolvedLoadoutEntry {
	slug: string;
	name: string;
	tier: LoadoutItem["tier"];
	justification: string;
	warbondSource: string;
	iconUrl: string | null;
	hoverIconUrl: string | null;
	wikiUrl: string | null;
	passiveName?: string;
	passiveIconUrl?: string | null;
	armorType?: Armor["type"];
}

export type WeaponIconSize = "sm" | "lg";

export function weaponIcon(
	w: Weapon,
	size: WeaponIconSize = "sm",
): string | null {
	if (!w.images.weapon) {
		return null;
	}
	const match = w.images.weapon.match(/^weapons\/(.+)\.png$/);
	if (!match) {
		return `/item-assets/${w.images.weapon}`;
	}
	return `/images/weapons/${match[1]}-${size}.webp`;
}

export function armorIcon(
	a: Armor,
	size: WeaponIconSize = "sm",
): string | null {
	return `/images/armor/${slugify(a.name)}-${size}.webp`;
}

function stratagemIcon(s: Stratagem): string | null {
	return s.icon ? `/item-assets/${s.icon}` : null;
}

function boosterIcon(b: Booster): string | null {
	return b.icon ? `/item-assets/${b.icon}` : null;
}

function passiveIcon(p: ArmorPassive): string | null {
	return p.icon ? `/item-assets/${p.icon}` : null;
}

export function resolveSection(
	sectionKey: keyof ClassLoadout,
	entries: Record<string, LoadoutItem>,
	items: UseItemsResult,
	cls: ClassDefinition,
): ResolvedLoadoutEntry[] {
	const result: ResolvedLoadoutEntry[] = [];
	const forbiddenWeights = new Set(cls.forbiddenArmorWeightClasses ?? []);

	for (const [slug, entry] of Object.entries(entries)) {
		if (sectionKey === "armorPassives") {
			const passive = items.passives.find((p) => slugify(p.name) === slug);
			if (!passive) {
				result.push({
					slug,
					name: slug,
					tier: entry.tier,
					justification: entry.justification,
					warbondSource: "",
					iconUrl: null,
					hoverIconUrl: null,
					wikiUrl: null,
				});
				continue;
			}
			for (const armor of passive.armors) {
				if (forbiddenWeights.has(armor.type)) {
					continue;
				}
				result.push({
					slug: slugify(armor.name),
					name: armor.name,
					tier: entry.tier,
					justification: entry.justification,
					warbondSource: armor.warbondSource,
					iconUrl: armorIcon(armor, "sm"),
					hoverIconUrl: armorIcon(armor, "lg"),
					wikiUrl: armor.url || passive.url || null,
					passiveName: passive.name,
					passiveIconUrl: passiveIcon(passive),
					armorType: armor.type,
				});
			}
			continue;
		}

		let name = slug;
		let warbondSource = "";
		let iconUrl: string | null = null;
		let hoverIconUrl: string | null = null;
		let wikiUrl: string | null = null;

		if (
			sectionKey === "primaries" ||
			sectionKey === "secondaries" ||
			sectionKey === "grenades"
		) {
			const weapon = items.weapons.find((w) => slugify(w.name) === slug);
			if (weapon) {
				name = weapon.name;
				warbondSource = weapon.warbondSource;
				iconUrl = weaponIcon(weapon, "sm");
				hoverIconUrl = weaponIcon(weapon, "lg");
				wikiUrl = weapon.url || null;
			}
		} else if (sectionKey === "stratagems") {
			const strat = items.stratagems.find((s) => slugify(s.name) === slug);
			if (strat) {
				name = strat.name;
				warbondSource = strat.warbondSource;
				iconUrl = stratagemIcon(strat);
				wikiUrl = strat.url || null;
			}
		} else if (sectionKey === "boosters") {
			const booster = items.boosters.find((b) => slugify(b.name) === slug);
			if (booster) {
				name = booster.name;
				warbondSource = booster.warbondSource;
				iconUrl = boosterIcon(booster);
				wikiUrl = booster.url || null;
			}
		}

		result.push({
			slug,
			name,
			tier: entry.tier,
			justification: entry.justification,
			warbondSource,
			iconUrl,
			hoverIconUrl,
			wikiUrl,
		});
	}

	const tierOrder = { signature: 0, preferred: 1, permitted: 2 };
	result.sort((a, b) => {
		const tierDiff = tierOrder[a.tier] - tierOrder[b.tier];
		if (tierDiff !== 0) {
			return tierDiff;
		}
		return a.name.localeCompare(b.name);
	});
	return result;
}

export function warbondSlug(warbondSource: string): string {
	return slugify(warbondSource);
}

export function getIconUrlForSlug(
	slug: string,
	items: UseItemsResult,
	weaponSize: WeaponIconSize = "sm",
): string | null {
	for (const s of items.stratagems) {
		if (slugify(s.name) === slug) {
			return stratagemIcon(s);
		}
	}
	for (const w of items.weapons) {
		if (slugify(w.name) === slug) {
			return weaponIcon(w, weaponSize);
		}
	}
	for (const b of items.boosters) {
		if (slugify(b.name) === slug) {
			return boosterIcon(b);
		}
	}
	for (const p of items.passives) {
		for (const a of p.armors) {
			if (slugify(a.name) === slug) {
				return armorIcon(a, weaponSize);
			}
		}
	}
	return null;
}

export function getShortNameForSlug(
	slug: string,
	items: UseItemsResult,
): string | null {
	for (const s of items.stratagems) {
		if (slugify(s.name) === slug) {
			return shortName(s.name);
		}
	}
	for (const w of items.weapons) {
		if (slugify(w.name) === slug) {
			return shortName(w.name);
		}
	}
	for (const b of items.boosters) {
		if (slugify(b.name) === slug) {
			return shortName(b.name);
		}
	}
	for (const p of items.passives) {
		for (const a of p.armors) {
			if (slugify(a.name) === slug) {
				return shortName(a.name);
			}
		}
	}
	return null;
}

export function getFullNameForSlug(
	slug: string,
	items: UseItemsResult,
): string | null {
	for (const s of items.stratagems) {
		if (slugify(s.name) === slug) {
			return s.name;
		}
	}
	for (const w of items.weapons) {
		if (slugify(w.name) === slug) {
			return w.name;
		}
	}
	for (const b of items.boosters) {
		if (slugify(b.name) === slug) {
			return b.name;
		}
	}
	for (const p of items.passives) {
		for (const a of p.armors) {
			if (slugify(a.name) === slug) {
				return a.name;
			}
		}
	}
	return null;
}

export function getArmorPassiveForSlug(
	slug: string,
	items: UseItemsResult,
): { passive: ArmorPassive; armor: Armor } | null {
	for (const p of items.passives) {
		for (const a of p.armors) {
			if (slugify(a.name) === slug) {
				return { passive: p, armor: a };
			}
		}
	}
	return null;
}
